import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAutenticado, usePrecisaEscolherEmpresa, useSemVinculo } from '../hooks/useSessao';

/**
 * Guarda de rota autenticada.
 *
 * Duas correções em relação ao legado:
 *
 * - **Fechado por padrão.** Assim como no backend (`@Publico()`), uma rota
 *   dentro deste elemento exige sessão; abrir é ato explícito. Esquecer a
 *   guarda não expõe a tela — ela simplesmente não é alcançada.
 * - **Empresa antes de número** (RN-603). Com mais de um vínculo e nenhuma
 *   escolha feita, a navegação vai ao seletor. O legado assumia `business[0]` e
 *   seguia direto para o painel.
 *
 * Esconder tela não é autorização: o servidor continua sendo quem decide o que
 * cada identidade pode ler (RN-412 a RN-418). Isto é experiência, não defesa.
 */
export function RotaProtegida(props: {
  readonly children?: ReactNode;
  /** `false` para telas que valem sem empresa escolhida (perfil, ajuda). */
  readonly exigeEmpresa?: boolean;
}): JSX.Element {
  const autenticado = useAutenticado();
  const precisaEscolher = usePrecisaEscolherEmpresa();
  const semEmpresa = useSemVinculo();
  const local = useLocation();

  if (!autenticado) {
    // O destino original viaja no state para o login devolver o usuário ao
    // lugar de onde ele veio, em vez de sempre jogá-lo no painel.
    return <Navigate to="/entrar" replace state={{ de: local.pathname }} />;
  }

  // Conta sem nenhum vínculo cai no mesmo lugar: lá a tela explica a situação,
  // em vez de o painel abrir vazio parecendo defeito.
  if (props.exigeEmpresa !== false && (precisaEscolher || semEmpresa)) {
    return <Navigate to="/selecionar-empresa" replace />;
  }

  return <>{props.children ?? <Outlet />}</>;
}
